import React, { useState } from 'react';
import axios from 'axios';
import { User, Building, Phone, Mail, MapPin, DollarSign, Calendar, FileText, CheckCircle, AlertCircle, LogOut, Plus, ChevronDown, ChevronUp, StickyNote, Trash2, Clock, CheckSquare, Square } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MobileForm = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [activeForm, setActiveForm] = useState('lead'); // lead, prospect, customer
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    contact: false,
    business: false,
    sales: false,
    notes: false,
    other: false
  });

  // Notes state
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');

  // Form data state
  const [formData, setFormData] = useState({
    company_name: '',
    corporation: '',
    first_name: '',
    last_name: '',
    job_title: '',
    email: '',
    phone: '',
    secondary_phone: '',
    secondary_email: '',
    fax: '',
    website: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    budget: '',
    project_type: '',
    square_footage: '',
    number_of_stories: '',
    timeline_urgency: '',
    source: '',
    referral_source: '',
    sales_rep: '',
    lead_score: '',
    expected_close_date: '',
    contract_value: '',
    follow_up_date: '',
    description: '',
    tags: '',
    grade: null,
    rating: '',
    assistant_first_name: '',
    assistant_last_name: '',
    assistant_phone: '',
    assistant_email: ''
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      setToken(response.data.token);
      setIsAuthenticated(true);
      setLoginError('');
    } catch (error) {
      if (error.response && error.response.status === 401) {
        setLoginError('Invalid password');
      } else {
        setLoginError('Cannot connect to server. Please check your internet connection or try again later.');
      }
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setToken('');
    setPassword('');
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      company_name: '',
      corporation: '',
      first_name: '',
      last_name: '',
      job_title: '',
      email: '',
      phone: '',
      secondary_phone: '',
      secondary_email: '',
      fax: '',
      website: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      budget: '',
      project_type: '',
      square_footage: '',
      number_of_stories: '',
      timeline_urgency: '',
      source: '',
      referral_source: '',
      sales_rep: '',
      lead_score: '',
      expected_close_date: '',
      contract_value: '',
      follow_up_date: '',
      description: '',
      tags: '',
      grade: null,
      rating: '',
      assistant_first_name: '',
      assistant_last_name: '',
      assistant_phone: '',
      assistant_email: ''
    });
    setNotes([]);
    setNewNote('');
  };

  // Add a new note with timestamp
  const addNote = () => {
    if (!newNote.trim()) return;
    const note = {
      id: Date.now().toString(),
      text: newNote.trim(),
      timestamp: new Date().toISOString()
    };
    setNotes(prev => [note, ...prev]);
    setNewNote('');
  };

  // Delete a note
  const deleteNote = (noteId) => {
    setNotes(prev => prev.filter(n => n.id !== noteId));
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    setSubmitSuccess(false);

    // Validate required fields
    if (!formData.company_name || !formData.first_name || !formData.last_name || !formData.email || !formData.phone) {
      setSubmitError('Please fill in all required fields (Company, First Name, Last Name, Email, Phone)');
      setIsSubmitting(false);
      return;
    }

    try {
      const endpoint = activeForm === 'lead' ? 'leads' : activeForm === 'prospect' ? 'prospects' : 'customers';
      
      // Clean up data - remove empty strings and convert numbers
      const cleanData = { ...formData };
      if (cleanData.lead_score) cleanData.lead_score = parseInt(cleanData.lead_score) || null;
      if (cleanData.rating) cleanData.rating = parseInt(cleanData.rating) || null;
      
      // Remove empty strings
      Object.keys(cleanData).forEach(key => {
        if (cleanData[key] === '') cleanData[key] = null;
      });

      // Add notes_list with proper format
      cleanData.notes_list = notes.map(note => ({
        id: note.id,
        text: note.text,
        timestamp: note.timestamp
      }));

      await axios.post(`${API}/admin/${endpoint}`, cleanData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSubmitSuccess(true);
      resetForm();
      
      // Auto-hide success message after 3 seconds
      setTimeout(() => setSubmitSuccess(false), 3000);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error:', error);
      setSubmitError(error.response?.data?.detail || 'Failed to save. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const FormSection = ({ title, section, icon: Icon, children }) => (
    <div className="mb-4 bg-white rounded-lg shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => toggleSection(section)}
        className="w-full px-4 py-3 flex items-center justify-between bg-slate-50 border-b"
      >
        <div className="flex items-center space-x-3">
          {expandedSections[section] ? (
            <CheckSquare size={20} className="text-orange-500" />
          ) : (
            <Square size={20} className="text-slate-400" />
          )}
          <Icon size={18} className="text-slate-600" />
          <span className="font-medium text-slate-800">{title}</span>
        </div>
        <span className={`text-xs px-2 py-1 rounded ${expandedSections[section] ? 'bg-orange-100 text-orange-600' : 'bg-slate-100 text-slate-500'}`}>
          {expandedSections[section] ? 'Open' : 'Closed'}
        </span>
      </button>
      {expandedSections[section] && (
        <div className="p-4 space-y-4 border-l-4 border-orange-500">
          {children}
        </div>
      )}
    </div>
  );

  const InputField = ({ label, name, type = 'text', required = false, placeholder = '' }) => (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type={type}
        name={name}
        value={formData[name]}
        onChange={handleInputChange}
        placeholder={placeholder}
        required={required}
        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-base"
      />
    </div>
  );

  const SelectField = ({ label, name, options, required = false }) => (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <select
        name={name}
        value={formData[name]}
        onChange={handleInputChange}
        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-base bg-white"
      >
        <option value="">Select...</option>
        {options.map(opt => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    </div>
  );

  const TextAreaField = ({ label, name, rows = 3 }) => (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
      <textarea
        name={name}
        value={formData[name]}
        onChange={handleInputChange}
        rows={rows}
        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-base"
      />
    </div>
  );

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-slate-800">3D Metal Home</h1>
            <p className="text-slate-600 text-sm">Mobile CRM Access</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-base"
                placeholder="Enter admin password"
                autoFocus
              />
            </div>
            {loginError && (
              <div className="flex items-center space-x-2 text-red-600 text-sm">
                <AlertCircle size={16} />
                <span>{loginError}</span>
              </div>
            )}
            <button
              type="submit"
              className="w-full bg-orange-500 hover:bg-orange-600 text-white font-medium py-3 px-4 rounded-lg transition-colors"
            >
              Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Main Form
  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="px-4 py-3 flex items-center justify-between">
          <h1 className="text-lg font-bold text-slate-800">Add New Entry</h1>
          <button onClick={handleLogout} className="text-slate-600 p-2">
            <LogOut size={20} />
          </button>
        </div>
        
        {/* Type Selector */}
        <div className="flex border-t">
          {['lead', 'prospect', 'customer'].map((type) => (
            <button
              key={type}
              onClick={() => setActiveForm(type)}
              className={`flex-1 py-3 text-sm font-medium capitalize transition-colors ${
                activeForm === type
                  ? type === 'lead' ? 'bg-blue-500 text-white' 
                    : type === 'prospect' ? 'bg-purple-500 text-white' 
                    : 'bg-orange-500 text-white'
                  : 'text-slate-600 bg-slate-50'
              }`}
            >
              {type}
            </button>
          ))}
        </div>
      </header>

      {/* Success/Error Messages */}
      {submitSuccess && (
        <div className="mx-4 mt-4 p-3 bg-green-100 border border-green-300 rounded-lg flex items-center space-x-2 text-green-800">
          <CheckCircle size={20} />
          <span className="font-medium">{activeForm.charAt(0).toUpperCase() + activeForm.slice(1)} added successfully!</span>
        </div>
      )}
      {submitError && (
        <div className="mx-4 mt-4 p-3 bg-red-100 border border-red-300 rounded-lg flex items-center space-x-2 text-red-800">
          <AlertCircle size={20} />
          <span>{submitError}</span>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="p-4">
        {/* Basic Information */}
        <FormSection title="Basic Information" section="basic" icon={User}>
          <InputField label="Company Name" name="company_name" required placeholder="Company name" />
          <InputField label="Corporation" name="corporation" placeholder="Corporation/LLC" />
          <div className="grid grid-cols-2 gap-3">
            <InputField label="First Name" name="first_name" required placeholder="First" />
            <InputField label="Last Name" name="last_name" required placeholder="Last" />
          </div>
          <InputField label="Job Title" name="job_title" placeholder="Position/Title" />
          <InputField label="Tags" name="tags" placeholder="Comma separated tags" />
        </FormSection>

        {/* Contact Information */}
        <FormSection title="Contact Information" section="contact" icon={Phone}>
          <InputField label="Email" name="email" type="email" required placeholder="email@example.com" />
          <InputField label="Phone" name="phone" type="tel" required placeholder="(555) 123-4567" />
          <InputField label="Secondary Phone" name="secondary_phone" type="tel" placeholder="Alt phone" />
          <InputField label="Secondary Email" name="secondary_email" type="email" placeholder="Alt email" />
          <InputField label="Fax" name="fax" type="tel" placeholder="Fax number" />
          <InputField label="Website" name="website" type="url" placeholder="https://..." />
          <InputField label="Address" name="address" placeholder="Street address" />
          <div className="grid grid-cols-2 gap-3">
            <InputField label="City" name="city" placeholder="City" />
            <InputField label="State" name="state" placeholder="State" />
          </div>
          <InputField label="Zip Code" name="zip_code" placeholder="ZIP" />
        </FormSection>

        {/* Business Information */}
        <FormSection title="Business Information" section="business" icon={Building}>
          <InputField label="Budget" name="budget" placeholder="$0.00" />
          <SelectField 
            label="Project Type" 
            name="project_type" 
            options={['Residential', 'Commercial', 'Industrial', 'Agricultural', 'Other']} 
          />
          <InputField label="Square Footage" name="square_footage" placeholder="sq ft" />
          <InputField label="Number of Stories" name="number_of_stories" placeholder="1, 2, 3..." />
          <SelectField 
            label="Timeline/Urgency" 
            name="timeline_urgency" 
            options={['Immediate', '1-3 months', '3-6 months', '6-12 months', '1+ year']} 
          />
        </FormSection>

        {/* Sales Information */}
        <FormSection title="Sales Information" section="sales" icon={DollarSign}>
          {/* Grade - 1 to 10 for all types */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Grade <span className="text-slate-500">(1-10, 10 is highest)</span>
            </label>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                <button
                  key={num}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, grade: num }))}
                  className={`w-8 h-8 rounded-full text-sm font-medium transition-all ${
                    formData.grade === num
                      ? 'bg-orange-500 text-white scale-110'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {num}
                </button>
              ))}
            </div>
          </div>
          <SelectField 
            label="Source" 
            name="source" 
            options={['Website', 'Referral', 'Trade Show', 'Cold Call', 'Social Media', 'Advertisement', 'Other']} 
          />
          <InputField label="Referral Source" name="referral_source" placeholder="Who referred?" />
          <InputField label="Sales Rep" name="sales_rep" placeholder="Assigned rep" />
          <InputField label="Lead Score" name="lead_score" type="number" placeholder="1-100" />
          <InputField label="Expected Close Date" name="expected_close_date" type="date" />
          <InputField label="Contract Value" name="contract_value" placeholder="$0.00" />
          <InputField label="Follow-up Date" name="follow_up_date" type="date" />
        </FormSection>

        {/* Notes Section */}
        <FormSection title={`Notes (${notes.length})`} section="notes" icon={StickyNote}>
          <div className="space-y-3">
            {/* Add New Note */}
            <div className="flex gap-2">
              <input
                type="text"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Add a note..."
                className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-base"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addNote())}
              />
              <button
                type="button"
                onClick={addNote}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
              >
                <Plus size={20} />
              </button>
            </div>

            {/* Notes List */}
            {notes.length > 0 ? (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {notes.map((note) => (
                  <div key={note.id} className="bg-slate-50 rounded-lg p-3 border">
                    <div className="flex justify-between items-start gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-1 text-xs text-slate-500 mb-1">
                          <Clock size={12} />
                          <span>{formatTimestamp(note.timestamp)}</span>
                        </div>
                        <p className="text-sm text-slate-800">{note.text}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => deleteNote(note.id)}
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-slate-400 text-sm py-4">No notes added yet</p>
            )}
          </div>
        </FormSection>

        {/* Other Information */}
        <FormSection title="Other Information" section="other" icon={FileText}>
          <TextAreaField label="Description" name="description" rows={4} />
          {(activeForm === 'prospect' || activeForm === 'customer') && (
            <>
              <div className="pt-2 border-t">
                <p className="text-sm font-medium text-slate-700 mb-3">Assistant Information</p>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <InputField label="First Name" name="assistant_first_name" placeholder="First" />
                    <InputField label="Last Name" name="assistant_last_name" placeholder="Last" />
                  </div>
                  <InputField label="Phone" name="assistant_phone" type="tel" placeholder="Phone" />
                  <InputField label="Email" name="assistant_email" type="email" placeholder="Email" />
                </div>
              </div>
            </>
          )}
        </FormSection>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className={`w-full py-4 rounded-lg font-medium text-white text-lg flex items-center justify-center space-x-2 ${
            activeForm === 'lead' ? 'bg-blue-500 hover:bg-blue-600' 
              : activeForm === 'prospect' ? 'bg-purple-500 hover:bg-purple-600' 
              : 'bg-orange-500 hover:bg-orange-600'
          } ${isSubmitting ? 'opacity-70' : ''} transition-colors`}
        >
          {isSubmitting ? (
            <span>Saving...</span>
          ) : (
            <>
              <Plus size={20} />
              <span>Add {activeForm.charAt(0).toUpperCase() + activeForm.slice(1)}</span>
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default MobileForm;
